import type { ComponentType, ReactNode, SVGAttributes } from 'react';

/** A leaf entry in the sidebar: one route. */
export type NavLinkItem = {
  id: string;
  title: string;
  /** In-app route path. Mutually exclusive with `href`. */
  to?: string;
  /** External URL. Opens in a new tab. */
  href?: string;
  /**
   * Extra route prefixes this entry also owns, for a screen whose path does not
   * sit under `to`.
   *
   * The row lights up for `to` and everything under it, which covers a detail
   * page nested in its list's path (`/invoices/vendor/12`). Since every route is
   * named after the section that opens it, that is enough for the navs as they
   * stand and no row declares this today - it is here for a screen that has to
   * live outside its section's segment. Without it the whole sidebar goes dark on
   * such a screen, and the group it belongs to collapses, which reads as "you have
   * left the section".
   *
   * Matched exactly as `to` is, longest claim wins - see `Sidebar`.
   */
  matchPaths?: string[];
  icon?: ComponentType<SVGAttributes<SVGSVGElement> & { size?: number }>;
  /** Permission NAMES; the entry is hidden unless the session holds one of them. */
  permissions?: string[];
  /**
   * Endpoint PATHS the entry's screen calls. The entry is hidden unless the session may use
   * at least one of them. This is the form the legacy nav config declares, and the one to
   * carry across when porting a screen — see CLAUDE.md > Authorization.
   */
  apiPath?: string[];
};

/** A collapsible group of leaf entries. */
export type NavGroupItem = {
  id: string;
  title: string;
  icon?: ComponentType<SVGAttributes<SVGSVGElement> & { size?: number }>;
  children: NavLinkItem[];
  permissions?: string[];
  apiPath?: string[];
};

export type NavItem = NavLinkItem | NavGroupItem;

export const isNavGroup = (item: NavItem): item is NavGroupItem => 'children' in item;

export type AppShellUser = {
  name: string;
  email?: string | null;
  avatarUrl?: string | null;
};

export type VerticalLayoutProps = {
  /** The portal's own sidebar definition. Each app owns its nav; the shell draws it. */
  navigation: NavItem[];
  /** Wordmark or logo for the sidebar header. Portal-specific, so it is passed in. */
  brand: ReactNode;
  user: AppShellUser | null;
  onSignOut: () => void;
  /** Route to the profile page. Differs per portal, so it is not hardcoded. */
  profilePath: string;
  /** Rendered in the navbar between the page title and the user chip. */
  navbarSlot?: ReactNode;
  /** Appended to every page's document title: `Customers · <suffix>`. */
  titleSuffix?: string;
};

/**
 * Route metadata the shell reads with `useMatches()` — set as a route's `handle`.
 *
 * Declared here rather than in the app's route table because the shell is what reads it, and
 * `shared/app-shell` may not import `src/app`.
 */
export type ShellRouteHandle = {
  /** The page title: the document title, and the last crumb. */
  title: string;
  /** Ancestor crumbs, nearest last. The current page is not repeated here. */
  crumbs?: { title: string; to?: string }[];
  /** Draws a back button beside the trail — the legacy `hasBack`. */
  hasBack?: boolean;
};
